import { ReactElement, ReactNode, RefCallback, useCallback, useEffect, useMemo, useRef, useState } from "react";
import SvgIcon from "@mui/material/SvgIcon";
import { CoreIconButton } from "@verity/components/core";
import { RecordingType } from "../../../../../types/general-types";
import i18n from "../../../../../i18n";
import SingleVideoLayout from "./SingleVideoLayout";
import VideoSideLayout from "./VideoSideLayout";
import VideoBottomLayout from "./VideoBottomLayout";
import styles from "./index.module.css";
import SideLayout from "../images/side-layout.svg?react";
import BottomLayout from "../images/bottom-layout.svg?react";
import ScrollLayout from "../images/scroll-layout.svg?react";

type LayoutMode = "side" | "bottom" | "single";

const layoutButtonTooltipSlotProps = {
	tooltip: {
		slotProps: {
			popper: { disablePortal: true, style: { maxWidth: "none" } },
			tooltip: { style: { maxWidth: "none", whiteSpace: "nowrap" as const } },
		},
	},
};

function LayoutButton({
	active,
	onClick,
	tooltip,
	children,
}: {
	active: boolean;
	onClick: () => void;
	tooltip: ReactNode;
	children: ReactNode;
}) {
	return (
		<CoreIconButton
			variant="plain"
			color="dark"
			className={`${styles.layoutButton} ${active ? styles.active : ""}`}
			onClick={onClick}
			tooltip={tooltip}
			slotProps={layoutButtonTooltipSlotProps}
		>
			<SvgIcon>{children}</SvgIcon>
		</CoreIconButton>
	);
}

function LayoutControls({ mode, onChange }: { mode: LayoutMode; onChange: (mode: LayoutMode) => void }) {
	return (
		<div className={styles.layoutControls}>
			<LayoutButton
				active={mode === "side"}
				onClick={() => onChange("side")}
				tooltip={i18n.t("result.components.studentSelection.videoPlayer.videoLayout.side")}
			>
				<SideLayout />
			</LayoutButton>
			<LayoutButton
				active={mode === "bottom"}
				onClick={() => onChange("bottom")}
				tooltip={i18n.t("result.components.studentSelection.videoPlayer.videoLayout.bottom")}
			>
				<BottomLayout />
			</LayoutButton>
			<LayoutButton
				active={mode === "single"}
				onClick={() => onChange("single")}
				tooltip={i18n.t("result.components.studentSelection.videoPlayer.videoLayout.single")}
			>
				<ScrollLayout />
			</LayoutButton>
		</div>
	);
}

function VideoWrapper({ children, targetContainer }: { children: ReactNode; targetContainer?: HTMLDivElement }) {
	const wrapperElementRef = useRef<HTMLDivElement | undefined>(undefined);
	const wrapperRef = useCallback(
		(node: HTMLDivElement | null) => {
			const newWrapperElement = node ?? undefined;
			if (newWrapperElement !== wrapperElementRef.current) {
				if (newWrapperElement) {
					targetContainer?.replaceChildren(newWrapperElement);
				}
				wrapperElementRef.current = newWrapperElement;
			}
		},
		[targetContainer],
	);

	return (
		<div className={styles.videoWrapper} ref={wrapperRef}>
			{children}
		</div>
	);
}

type VideoContainerRef = RefCallback<HTMLDivElement>;
export default function VideoLayout({
	videoTemplate,
	videoPlayers,
}: {
	videoTemplate: Partial<Record<RecordingType, number>>;
	videoPlayers: Partial<Record<RecordingType, ReactElement[]>>;
}) {
	const [mode, setMode] = useState<LayoutMode>("side");

	const [videoContainers, setVideoContainers] = useState<
		Partial<Record<RecordingType, (HTMLDivElement | undefined)[]>>
	>(
		Object.fromEntries(
			Object.entries(videoTemplate).map(([type, count]) => [
				type as RecordingType,
				Array.from({ length: count }, (_) => undefined),
			]),
		),
	);
	useEffect(() => {
		setVideoContainers((currContainers) =>
			Object.fromEntries(
				Object.entries(currContainers).map(([typeKey, typeContainers]) => {
					const type = typeKey as RecordingType;
					const newCount = videoTemplate[type] ?? 0;
					if (newCount === typeContainers.length) {
						return [type, typeContainers];
					} else {
						return [type, Array.from({ length: newCount }, (_) => undefined)];
					}
				}),
			),
		);
	}, [videoTemplate]);
	const videoContainerRefs: Partial<Record<RecordingType, VideoContainerRef[]>> = useMemo(
		() =>
			Object.fromEntries(
				Object.entries(videoTemplate).map(([typeKey, count]) => {
					const type = typeKey as RecordingType;
					return [
						type,
						Array.from({ length: count }).map((_, index) => (elem) => {
							const newElement = elem ?? undefined;
							setVideoContainers((currContainers) => {
								const typeContainers = currContainers[type];
								if (!typeContainers || index >= typeContainers.length || typeContainers[index] === newElement) {
									return currContainers;
								}
								return {
									...currContainers,
									[type]: typeContainers
										.slice(0, index)
										.concat([newElement])
										.concat(typeContainers.slice(index + 1)),
								};
							});
						}),
					];
				}),
			),
		[videoTemplate],
	);
	const { webcam = [], screencapture = [] } = videoContainerRefs;
	const allRefs = webcam.concat(screencapture);

	const displayMode = allRefs.length < 2 ? "single" : mode;

	let layout: ReactNode;
	if (displayMode === "single") {
		layout = <SingleVideoLayout videoRefs={allRefs} />;
	} else {
		let mainVideo: VideoContainerRef;
		let auxillaryVideos: VideoContainerRef[];
		if (screencapture.length > 0) {
			mainVideo = screencapture[0];
			auxillaryVideos = webcam;
		} else {
			[mainVideo, ...auxillaryVideos] = webcam;
		}

		if (displayMode === "side") {
			layout = <VideoSideLayout mainVideoRef={mainVideo} sideVideoRefs={auxillaryVideos} />;
		} else {
			layout = <VideoBottomLayout mainVideoRef={mainVideo} bottomVideoRefs={auxillaryVideos} />;
		}
	}

	return (
		<div className={styles.videoContainer}>
			{Object.entries(videoPlayers).flatMap(([type, typeVideos]) =>
				typeVideos.map((video, index) => {
					const container = videoContainers[type as RecordingType]?.[index];
					return (
						<VideoWrapper key={`${type}_${index}`} targetContainer={container}>
							{video}
						</VideoWrapper>
					);
				}),
			)}
			{allRefs.length > 1 && <LayoutControls mode={mode} onChange={setMode} />}
			{layout}
		</div>
	);
}
